/**
 * DeleteMessageDialog Component
 * 
 * Confirmation dialog for deleting messages.
 * Shows "Delete for Everyone" option with a warning about the action.
 */

import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDesign } from '@/contexts/DesignContext';

export default function DeleteMessageDialog({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isDeleting = false,
  isOwnMessage = false
}) {
  const { isStudio } = useDesign();
  if (!isOpen) return null;

  return (
    <div 
      data-ui="scrim"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="delete-message-dialog-overlay"
    >
      <div 
        data-ui="float" className="bg-card rounded-2xl shadow-xl max-w-sm w-full mx-4 overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-message-dialog"
      >
        {/* Header */}
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-3">
            <div data-ui="dialog-icon-tile" className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h3 data-ui="dlg-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {isStudio ? 'Delete message' : 'Delete Message'}
            </h3>
          </div>
          
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {isOwnMessage 
              ? "This message will be deleted for everyone in this conversation. This action cannot be undone."
              : "This message will be deleted from your view. Others can still see it."
            }
          </p>
        </div>

        {/* Actions */}
        <div data-ui="dlg-f" className="px-6 pb-6 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1"
            data-testid="delete-dialog-cancel"
          >
            Cancel
          </Button>
          
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 bg-red-500 hover:bg-red-600"
            data-testid="delete-dialog-confirm"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
